// @flow

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import { push } from "connected-react-router";
import { route } from "@routes/helper";
import { isFirstCareUser } from "@modules/account/selectors";
import actionCreators from "@modules/service/actionCreator";
import type { ReasonSpecificScript } from "@modules/service/reducer/reason-specific-scripts/types";
import type {
  HierarchyAssignment,
  ApprovableContainer,
  HierarchyAssignmentSummary,
} from "@modules/service/types";
import {
  GetReasonSpecificScripts,
  AddReasonSpecificScript,
  GetReasonSpecificScript,
  StartReasonSpecificScriptDraft,
  UpdateReasonSpecificScriptAssignments,
  UpdateReasonSpecificScript,
  DiscardReasonSpecificScriptDraft,
  CancelReasonSpecificScriptReview,
  CancelReasonSpecificScriptReviewAndDiscard,
  DuplicateReasonSpecificScript,
  SubmitReasonSpecificScriptForReview,
  DisableReasonSpecificScript,
} from "@modules/service/constants/actions";
import { ApprovalStateEnum } from "@modules/service/constants";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { sortToString, transformSort } from "@transformers/sort";

const URL_PATH = clientId =>
  `/api/v1/service-admin/client/${clientId}/service-config/absence-management/reason-script/hierarchy-script`;
const CONFIG = {
  headers: {
    Accept: "application/json",
  },
};

type ListRequest = $Exact<{
  clientId: string,
  page: number,
  search: Object,
  sort: Object,
}>;

export type ReasonSpecificRequest = $Exact<{
  ...ListRequest,
  stableId: string,
}>;

export type DeparmentSpecificScriptSubmitForReviewRequest = $Exact<{
  ...ReasonSpecificRequest,
  notes: string,
}>;

type ReasonSpecificScriptDisableRequest = $Exact<{
  clientId: string,
  providerId: string,
  approvable: ?Object,
  isActive: ?boolean,
}>;

type DeparmentSpecificScriptUpdateRequest = $Exact<{
  ...ReasonSpecificRequest,
  draft: ReasonSpecificScript,
}>;

type AddDeparmentSpecificScriptRequest = $Exact<{
  clientId: string,
  draft: ReasonSpecificScript,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onGetReasonSpecificScripts({
  payload,
}: Action<ListRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(http.get, URL_PATH(payload.clientId), {
      ...CONFIG,
      params: {
        limit: 0,
        page: payload.page ? payload.page : 0,
        sort: payload.sort ? sortToString(transformSort(payload.sort)) : "",
      },
    });
    yield put(actionCreators.GetReasonSpecificScriptsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetReasonSpecificScriptsError(e.response));
  }
}

// REFRESH

type RefreshRequest = $Exact<{
  clientId: string,
  stableId?: string,
}>;

export function* onRefresh(params: RefreshRequest): Generator<*, void, Object> {
  try {
    yield put(actionCreators.GetReasonSpecificScripts({ clientId: params.clientId }));
    if (params.stableId) {
      yield put(
        actionCreators.GetReasonSpecificScript({
          clientId: params.clientId,
          stableId: params.stableId,
        }),
      );
    }
  } catch (e) {
    throw e;
  }
}

export function* onAddReasonSpecificScript({
  payload,
}: Action<AddDeparmentSpecificScriptRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.draft || !payload.assignments) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(http.post, URL_PATH(payload.clientId), payload.draft);

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((assignment: HierarchyAssignment) => {
          return call(
            http.post,
            `${URL_PATH(payload.clientId)}/${response.data.id}/assignment`,
            assignment,
          );
        }),
      ]);
    }

    const firstCareUser = yield select(isFirstCareUser);
    if (firstCareUser) {
      yield call(http.post, `${URL_PATH(payload.clientId)}/${response.data.id}/submit-for-review`, {
        notes: "Automatically submitted",
      });
    }

    yield all([
      put(actionCreators.GetReasonSpecificScriptActiveSuccess(response.data)),
      put(actionCreators.GetReasonSpecificScripts({ clientId: payload.clientId })),
      put(actionCreators.ShowAddReasonSpecificScriptModal({ show: false, step: 1 })),
      put(sendSuccessMessage("DRAFT_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.AddReasonSpecificScriptError(e.response));
  }
}

export function* onGetReasonSpecificScript({
  payload,
}: Action<ReasonSpecificRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    const url = `${URL_PATH(payload.clientId)}/${payload.stableId}`;
    const response = yield call(http.get, url, CONFIG);
    const assignments = yield call(http.get, `${url}/assignment`, CONFIG);

    yield put(actionCreators.GetReasonSpecificScriptSuccess(response.data));
    yield put(actionCreators.SetReasonSpecificScriptAssignments(assignments.data));
  } catch (e) {
    yield put(actionCreators.GetReasonSpecificScriptError(e.response));
  }
}

export function* onStartReasonSpecificScriptDraft({
  payload,
}: Action<ReasonSpecificRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    const url = `${URL_PATH(payload.clientId)}/${payload.stableId}`;
    const response = yield call(http.get, url, CONFIG);

    yield all([
      put(actionCreators.StartReasonSpecificScriptDraftSuccess(response.data)),
      put(actionCreators.ShowReasonSpecificScriptEditModal(true)),
    ]);
  } catch (e) {
    yield put(actionCreators.StartReasonSpecificScriptDraftError(e.response));
  }
}

type ReasonSpecificScriptUpdateAssignmentsRequest = $Exact<{
  container: ApprovableContainer<ReasonSpecificScript>,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onUpdateReasonSpecificScriptAssignments({
  payload,
}: Action<ReasonSpecificScriptUpdateAssignmentsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.container || !payload.assignments) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `${URL_PATH(payload.container.clientId)}/${payload.container.id}/assignment`,
      CONFIG,
    );
    let prevAssignments = response.data;

    if (prevAssignments && prevAssignments.length) {
      yield all([
        ...prevAssignments.map((a: HierarchyAssignmentSummary) => {
          return call(
            http.delete,
            `${URL_PATH(payload.container.clientId)}/${payload.container.id}/assignment/${a.id}`,
            CONFIG,
          );
        }),
      ]);
    }

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `${URL_PATH(payload.container.clientId)}/${payload.container.id}/assignment`,
            a,
            CONFIG,
          );
        }),
      ]);
    }

    yield all([
      put(actionCreators.GetReasonSpecificScripts({ clientId: payload.container.clientId })),
      put(actionCreators.ShowEditReasonSpecificScriptAssignmentsModal(false)),
      put(sendSuccessMessage("HIERARCHY_SCOPE_UPDATED")),
      put(
        push(
          route("ADMINISTRATION_SERVICE_DEFAULT_STANDARD_SCRIPTS", {
            clientId: payload.container.clientId,
          }),
        ),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateReasonSpecificScriptAssignmentsError(e.message));
  }
}

export function* onUpdateReasonSpecificScript({
  payload,
}: Action<DeparmentSpecificScriptUpdateRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.stableId || !payload.draft) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.put,
      `${URL_PATH(payload.clientId)}/${payload.stableId}/draft`,
      payload.draft,
      CONFIG,
    );
    const firstCareUser = yield select(isFirstCareUser);

    if (firstCareUser) {
      yield call(
        http.post,
        `${URL_PATH(payload.clientId)}/${payload.stableId}/submit-for-review`,
        {
          notes: "Automatically submitted",
        },
        CONFIG,
      );
    }

    yield all([
      put(actionCreators.UpdateReasonSpecificScriptSuccess(response.data)),
      put(actionCreators.ShowReasonSpecificScriptEditModal(false)),
      put(actionCreators.GetReasonSpecificScripts({ clientId: payload.clientId })),
      put(
        push(
          route("ADMINISTRATION_SERVICE_DEFAULT_STANDARD_SCRIPTS", {
            clientId: payload.clientId,
          }),
        ),
      ),
      put(sendSuccessMessage("DRAFT_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateReasonSpecificScriptError(e.response));
  }
}

export function* onDiscardReasonSpecificScriptDraft({
  payload,
}: Action<ReasonSpecificRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    yield call(http.delete, `${URL_PATH(payload.clientId)}/${payload.stableId}/draft`, CONFIG);

    yield all([
      put(actionCreators.DiscardReasonSpecificScriptDraftSuccess()),
      put(actionCreators.SetShowDiscardReasonSpecificScriptModal(false)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_DEFAULT_STANDARD_SCRIPTS", {
            clientId: payload.clientId,
          }),
        ),
      ),
      put(sendSuccessMessage("DRAFT_DISCARDED")),
    ]);
    yield call(onRefresh, { clientId: payload.clientId });
  } catch (e) {
    yield put(actionCreators.DiscardReasonSpecificScriptDraftError(e.response));
  }
}

export function* onCancelReasonSpecificScriptReview({
  payload,
}: Action<ReasonSpecificRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `${URL_PATH(payload.clientId)}/${payload.stableId}/cancel-review`,
      null,
      CONFIG,
    );

    yield all([
      put(actionCreators.CancelReasonSpecificScriptReviewSuccess()),
      put(actionCreators.SetShowCancelReasonSpecificScriptModal(false)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_DEFAULT_STANDARD_SCRIPTS", {
            clientId: payload.clientId,
          }),
        ),
      ),
      put(sendSuccessMessage("DRAFT_CANCELED")),
    ]);
    yield call(onRefresh, { clientId: payload.clientId });
  } catch (e) {
    yield put(actionCreators.CancelReasonSpecificScriptReviewError(e.response));
  }
}

export function* onCancelReasonSpecificScriptReviewAndDiscard({
  payload,
}: Action<ReasonSpecificRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `${URL_PATH(payload.clientId)}/${payload.stableId}/cancel-review`,
      null,
      CONFIG,
    );
    yield call(http.delete, `${URL_PATH(payload.clientId)}/${payload.stableId}/draft`, CONFIG);

    yield all([
      put(actionCreators.CancelReasonSpecificScriptReviewSuccess()),
      put(actionCreators.SetShowCancelDiscardReasonSpecificScriptModal(false)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_DEFAULT_STANDARD_SCRIPTS", {
            clientId: payload.clientId,
          }),
        ),
      ),
      put(sendSuccessMessage("DRAFT_CANCELED_AND_DISCARDED")),
    ]);
    yield call(onRefresh, { clientId: payload.clientId });
  } catch (e) {
    yield put(actionCreators.CancelReasonSpecificScriptReviewAndDiscardError(e.response));
  }
}

export function* onDuplicateReasonSpecificScript({
  payload,
}: Action<ReasonSpecificRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `${URL_PATH(payload.clientId)}/${payload.stableId}/active`,
      CONFIG,
    );

    yield all([
      put(actionCreators.DuplicateReasonSpecificScriptSuccess(response.data)),
      put(actionCreators.SetReasonSpecificScriptAssignments([])),
      put(actionCreators.ShowAddReasonSpecificScriptModal({ show: true, step: 1 })),
      put(
        push(
          route("ADMINISTRATION_SERVICE_DEFAULT_STANDARD_SCRIPTS", {
            clientId: payload.clientId,
          }),
        ),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.DuplicateReasonSpecificScriptError(e.response));
  }
}

export function* onSubmitReasonSpecificScriptForReview({
  payload,
}: Action<DeparmentSpecificScriptSubmitForReviewRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.stableId || !payload.notes) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `${URL_PATH(payload.clientId)}/${payload.stableId}/submit-for-review`,
      {
        notes: payload.notes,
      },
      CONFIG,
    );

    yield all([
      put(actionCreators.SubmitReasonSpecificScriptForReviewSuccess()),
      put(actionCreators.SetShowSubmitReasonSpecificScriptModal(false)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_DEFAULT_STANDARD_SCRIPTS", {
            clientId: payload.clientId,
          }),
        ),
      ),
      put(sendSuccessMessage("DRAFT_SUBMITTED")),
    ]);
  } catch (e) {
    yield put(actionCreators.SubmitReasonSpecificScriptForReviewError(e.response));
  }
}

export function* onDisableReasonSpecificScript({
  payload,
}: Action<ReasonSpecificScriptDisableRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.providerId || !payload.approvable) {
      throw Error("Invalid payload provided");
    }

    const draft =
      payload.approvable.approvalState === ApprovalStateEnum.ACTIVE_ONLY
        ? payload.approvable.active
        : payload.approvable.draft;

    if (!draft) {
      throw Error("Invalid draft");
    }

    const response = yield call(
      http.put,
      `${URL_PATH(payload.clientId)}/${payload.providerId}/draft`,
      {
        ...draft,
        isActive: payload.isActive,
      },
      CONFIG,
    );

    yield call(
      http.post,
      `${URL_PATH(payload.clientId)}/${payload.providerId}/submit-for-review`,
      {
        notes: "Reason specific script disabled and resubmitted for review",
      },
      CONFIG,
    );

    yield call(onRefresh, { clientId: payload.clientId });
    yield all([
      put(actionCreators.DisableReasonSpecificScriptSuccess(response.data)),
      put(actionCreators.SetShowDisableReasonSpecificScriptModal(false)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_DEFAULT_STANDARD_SCRIPTS", {
            clientId: payload.clientId,
          }),
        ),
      ),
      put(sendSuccessMessage("DRAFT_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.DisableReasonSpecificScriptError(e.response));
  }
}

export function* ReasonSpecificScriptsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetReasonSpecificScripts, onGetReasonSpecificScripts),
    takeEvery(AddReasonSpecificScript, onAddReasonSpecificScript),
    takeEvery(GetReasonSpecificScript, onGetReasonSpecificScript),
    takeEvery(StartReasonSpecificScriptDraft, onStartReasonSpecificScriptDraft),
    takeEvery(UpdateReasonSpecificScriptAssignments, onUpdateReasonSpecificScriptAssignments),
    takeEvery(UpdateReasonSpecificScript, onUpdateReasonSpecificScript),
    takeEvery(DiscardReasonSpecificScriptDraft, onDiscardReasonSpecificScriptDraft),
    takeEvery(CancelReasonSpecificScriptReview, onCancelReasonSpecificScriptReview),
    takeEvery(
      CancelReasonSpecificScriptReviewAndDiscard,
      onCancelReasonSpecificScriptReviewAndDiscard,
    ),
    takeEvery(DuplicateReasonSpecificScript, onDuplicateReasonSpecificScript),
    takeEvery(SubmitReasonSpecificScriptForReview, onSubmitReasonSpecificScriptForReview),
    takeEvery(DisableReasonSpecificScript, onDisableReasonSpecificScript),
  ]);
}

export default ReasonSpecificScriptsSagas;
